import is from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';

import type { ActiveThreadStore } from './active-thread-store';
import { createActiveThreadStore } from './active-thread-store';
import { CommentsScopeContext } from './comments-scope-context';
import { itemDetailsCache } from './item-details-cache';
import { useDeactivateOnOutsideClick } from './use-deactivate-on-outside-click';

export const ActiveThreadStoreContext = createContext<StoreApi<ActiveThreadStore> | null>(null);

type ActiveThreadStoreProviderProps = {
    readonly initialIsCommentsOpen?: boolean;
    readonly initialStore?: StoreApi<ActiveThreadStore>;
};

const OutsideClickDeactivation: FunctionComponent = () => {
    useDeactivateOnOutsideClick();
    return null;
};

const ScopeChangeReset: FunctionComponent = () => {
    const scope = useContext(CommentsScopeContext);
    const setActiveThreadId = useActiveThreadStore((state) => state.setActiveThreadId);
    const setCommentClassification = useActiveThreadStore((state) => state.setCommentClassification);
    const setViewMode = useActiveThreadStore((state) => state.setViewMode);
    const cancelPending = useActiveThreadStore((state) => state.cancelPending);
    const scopeKey = is.null(scope)
        ? null
        : `${scope.architectureId}|${scope.itemId}|${scope.version}|${scope.language}`;
    const previousScopeKeyRef = useRef<string | null>(scopeKey);

    useEffect(() => {
        if (is.null(scopeKey)) return;
        if (previousScopeKeyRef.current === scopeKey) return;
        previousScopeKeyRef.current = scopeKey;
        cancelPending();
        setActiveThreadId(null);
        setCommentClassification(null);
        setViewMode('active');
    }, [scopeKey, cancelPending, setActiveThreadId, setCommentClassification, setViewMode]);

    return null;
};

const RestoreDetailsBinding: FunctionComponent<{ readonly store: StoreApi<ActiveThreadStore> }> = ({ store }) => {
    const queryClient = useQueryClient();
    useEffect(() => {
        store.getState().setRestoreDetails((snapshot) => {
            itemDetailsCache.restore(queryClient, snapshot);
        });
        store.getState().setInvalidateDetails(({ architectureId, itemId, version }) => {
            itemDetailsCache.invalidate(queryClient, architectureId, itemId, version, true);
        });
        return () => {
            store.getState().setRestoreDetails(null);
            store.getState().setInvalidateDetails(null);
        };
    }, [store, queryClient]);
    return null;
};

export const ActiveThreadStoreProvider: FunctionComponent<PropsWithChildren<ActiveThreadStoreProviderProps>> = ({
    children,
    initialIsCommentsOpen,
    initialStore,
}) => {
    const storeRef = useRef<StoreApi<ActiveThreadStore> | null>(null);
    storeRef.current ??= initialStore ?? createActiveThreadStore({ isCommentsOpen: initialIsCommentsOpen });

    return (
        <ActiveThreadStoreContext.Provider value={storeRef.current}>
            <RestoreDetailsBinding store={storeRef.current} />
            <OutsideClickDeactivation />
            <ScopeChangeReset />
            {children}
        </ActiveThreadStoreContext.Provider>
    );
};

export const useActiveThreadStore = <T,>(selector: (state: ActiveThreadStore) => T): T => {
    const store = useContext(ActiveThreadStoreContext);

    if (is.null(store)) {
        throw new Error('useActiveThreadStore must be used inside <ActiveThreadStoreProvider>');
    }

    return useStore(store, selector);
};
